/**
 * Dashboard Service
 * Centralized service for dashboard data management and caching
 * Uses Supabase database functions for real business metrics
 */

import { supabase } from './supabase';
import { 
  clearVercelCache, 
  getVercelMetricsForDashboard, 
  testVercelConnection 
} from './vercelService';

/**
 * Clear all dashboard-related caches
 */
export const clearDashboardCache = () => {
  // Clear Vercel cache
  clearVercelCache();
  
  // Clear any other dashboard caches here in the future
  console.log('Dashboard cache cleared');
};

/**
 * Get dashboard status
 */
export const getDashboardStatus = () => {
  return {
    lastCleared: new Date().toISOString(),
    cacheStatus: 'cleared'
  };
};

/**
 * Get Vercel metrics for dashboard display
 */
export const getVercelMetrics = async () => {
  try {
    return await getVercelMetricsForDashboard();
  } catch (error) {
    console.error('Error getting Vercel metrics:', error);
    throw error;
  }
};

/**
 * Get Vercel connection status
 */
export const getVercelConnectionStatus = async () => {
  try {
    return await testVercelConnection();
  } catch (error) {
    console.error('Error testing Vercel connection:', error);
    return {
      success: false,
      error: error.message,
      config: {
        hasToken: false,
        hasProjectId: false
      }
    };
  }
};

/**
 * Get executive summary metrics from database
 */
export const getExecutiveSummary = async () => {
  try {
    const { data, error } = await supabase.rpc('get_saas_metrics_summary');
    
    if (error) {
      console.error('Error fetching SaaS metrics:', error);
      // Return fallback data if database function fails
      return {
        mrr: 0,
        mrrGrowth: 0,
        activeSubscribers: 0,
        subscriberGrowth: 0,
        churnRate: 0,
        arpu: 0,
        totalUsers: 0
      };
    }

    return {
      mrr: data?.mrr || 0,
      mrrGrowth: 0, // Would need historical data for growth calculation
      activeSubscribers: data?.active_subscriptions || 0,
      subscriberGrowth: 0, // Would need historical data
      churnRate: data?.churn_rate || 0,
      arpu: data?.mrr && data?.active_subscriptions ? (data.mrr / data.active_subscriptions) : 0,
      totalUsers: data?.mau || 0 // Using MAU as total active users
    };
  } catch (error) {
    console.error('Error getting executive summary:', error);
    throw error;
  }
};

/**
 * Get system alerts from database
 */
export const getSystemAlerts = async () => {
  try {
    const { data: alerts, error } = await supabase
      .from('system_alerts')
      .select('*')
      .eq('is_resolved', false)
      .order('created_at', { ascending: false })
      .limit(10);

    if (error) {
      console.error('Error fetching system alerts:', error);
      // Return fallback data as flat array
      return [
        {
          id: 'fallback-1',
          type: 'warning',
          title: 'Database Migration Needed',
          message: 'Dashboard enhancement migration not yet applied',
          created_at: new Date().toISOString()
        }
      ];
    }

    // Ensure alerts is an array and return it directly
    return Array.isArray(alerts) ? alerts : [];
  } catch (error) {
    console.error('Error getting system alerts:', error);
    // Return safe fallback as flat array instead of throwing
    return [
      {
        id: 'error-1',
        type: 'warning',
        title: 'System Alert Error',
        message: 'Unable to fetch system alerts: ' + error.message,
        created_at: new Date().toISOString()
      }
    ];
  }
};

/**
 * Get recent activity from multiple sources (system alerts, games, user changes, etc.)
 */
export const getRecentActivity = async (limit = 10) => {
  try {
    console.log('Fetching recent activity from multiple sources...');
    
    // Fetch from multiple activity sources
    const [alertsResult, gamesResult, subscriptionsResult, usersResult, contactsResult] = await Promise.all([
      // System alerts (urgent issues)
      supabase
        .from('system_alerts')
        .select('id, type, title, message, created_at')
        .order('created_at', { ascending: false })
        .limit(5),
      
      // Recent games added
      supabase
        .from('games')
        .select('id, title, created_at, gaming_studios(name)')
        .eq('status', 'published')
        .order('created_at', { ascending: false })
        .limit(5),
      
      // Recent subscription changes
      supabase
        .from('subscriptions')
        .select('id, status, created_at, updated_at, user_profiles(email)')
        .order('updated_at', { ascending: false })
        .limit(5),
      
      // Recent user registrations
      supabase
        .from('user_profiles')
        .select('id, email, role, created_at, updated_at')
        .order('created_at', { ascending: false })
        .limit(5),
      
      // Recent support tickets (contacts) - include both new and recently updated
      supabase
        .from('contacts')
        .select('id, subject, issue_type, priority, status, created_at, updated_at, resolved_at, user_profiles(email)')
        .order('updated_at', { ascending: false })
        .limit(10)
    ]);

    console.log('Activity sources results:', {
      alerts: alertsResult.data?.length,
      games: gamesResult.data?.length,
      subscriptions: subscriptionsResult.data?.length,
      users: usersResult.data?.length,
      contacts: contactsResult.data?.length
    });

    const activities = [];

    // Process system alerts
    if (alertsResult.data) {
      alertsResult.data.forEach(alert => {
        activities.push({
          id: `alert-${alert.id}`,
          type: alert.type === 'error' ? 'system_alert' : 'system_info',
          message: alert.title,
          user: 'System',
          timestamp: alert.created_at,
          priority: alert.type === 'error' ? 'high' : 'medium'
        });
      });
    }

    // Process new games
    if (gamesResult.data) {
      gamesResult.data.forEach(game => {
        activities.push({
          id: `game-${game.id}`,
          type: 'game_published',
          message: `New game "${game.title}" published`,
          user: game.gaming_studios?.name || 'Unknown Studio',
          timestamp: game.created_at,
          priority: 'medium'
        });
      });
    }

    // Process subscription changes
    if (subscriptionsResult.data) {
      subscriptionsResult.data.forEach(sub => {
        const isNew = new Date(sub.created_at).getTime() === new Date(sub.updated_at).getTime();
        activities.push({
          id: `sub-${sub.id}`,
          type: isNew ? 'subscription_created' : 'subscription_updated',
          message: isNew 
            ? `New subscription created` 
            : `Subscription ${sub.status}`,
          user: sub.user_profiles?.email || 'Unknown User',
          timestamp: sub.updated_at,
          priority: sub.status === 'cancelled' ? 'high' : 'low'
        });
      });
    }

    // Process user registrations and role changes
    if (usersResult.data) {
      usersResult.data.forEach(user => {
        const isRoleChange = new Date(user.created_at).getTime() !== new Date(user.updated_at).getTime();
        if (isRoleChange && user.role === 'admin') {
          activities.push({
            id: `user-role-${user.id}`,
            type: 'user_role_change',
            message: `User promoted to admin`,
            user: user.email,
            timestamp: user.updated_at,
            priority: 'high'
          });
        } else if (!isRoleChange) {
          activities.push({
            id: `user-reg-${user.id}`,
            type: 'user_registration',
            message: `New user registered`,
            user: user.email,
            timestamp: user.created_at,
            priority: 'low'
          });
        }
      });
    }

    // Process support tickets (contacts)
    if (contactsResult.data) {
      contactsResult.data.forEach(contact => {
        const priorityLevel = contact.priority === 'urgent' || contact.priority === 'high' ? 'high' : 
                             contact.priority === 'normal' ? 'medium' : 'low';
        const issueTypeFormatted = contact.issue_type.replace(/_/g, ' ');
        
        // Determine if this is a new ticket or a status update
        const isNewTicket = new Date(contact.created_at).getTime() === new Date(contact.updated_at).getTime();
        const isResolved = contact.status === 'resolved' && contact.resolved_at;
        
        let activityType, message, timestamp;
        
        if (isResolved && !isNewTicket) {
          // This is a resolved ticket
          activityType = 'support_resolved';
          message = `Support ticket resolved: ${contact.subject}`;
          timestamp = contact.resolved_at || contact.updated_at;
        } else if (!isNewTicket) {
          // This is a status update
          activityType = 'support_updated';
          message = `Support ticket ${contact.status}: ${contact.subject}`;
          timestamp = contact.updated_at;
        } else {
          // This is a new ticket
          activityType = contact.priority === 'urgent' ? 'support_urgent' : 'support_ticket';
          message = `${contact.priority.toUpperCase()} support ticket: ${contact.subject}`;
          timestamp = contact.created_at;
        }
        
        activities.push({
          id: `contact-${contact.id}-${contact.status}`,
          type: activityType,
          message: message,
          user: contact.user_profiles?.email || 'Anonymous User',
          timestamp: timestamp,
          priority: isResolved ? 'medium' : priorityLevel,
          metadata: {
            issueType: issueTypeFormatted,
            status: contact.status,
            contactId: contact.id
          }
        });
      });
    }

    // Sort all activities by timestamp and limit
    const sortedActivities = activities
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, limit);

    console.log('Combined activities:', sortedActivities.length);

    // Return fallback if no real activities
    if (sortedActivities.length === 0) {
      return [
        {
          id: 'fallback-1',
          type: 'system_info',
          message: 'No recent platform activity',
          user: 'System',
          timestamp: new Date().toISOString(),
          priority: 'low'
        },
        {
          id: 'fallback-2',
          type: 'system_info',
          message: 'Activity feed will populate as users interact with the platform',
          user: 'System',
          timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
          priority: 'low'
        }
      ];
    }

    return sortedActivities;
  } catch (error) {
    console.error('Error getting recent activity:', error);
    // Return safe fallback instead of throwing
    return [
      {
        id: 'error-1',
        type: 'error',
        message: 'Unable to fetch recent activity: ' + error.message,
        user: 'System',
        timestamp: new Date().toISOString(),
        priority: 'high'
      }
    ];
  }
};

/**
 * Create system alert
 */
export const createSystemAlert = async (alert) => {
  try {
    const { data, error } = await supabase
      .from('system_alerts')
      .insert([{
        type: alert.type,
        title: alert.title,
        message: alert.message,
        metadata: alert.metadata || {}
      }])
      .select()
      .single();

    if (error) {
      console.error('Error creating system alert:', error);
      throw error;
    }

    return { success: true, id: data.id };
  } catch (error) {
    console.error('Error creating system alert:', error);
    throw error;
  }
};

/**
 * Resolve system alert
 */
export const resolveSystemAlert = async (alertId) => {
  try {
    const { error } = await supabase
      .from('system_alerts')
      .update({
        is_resolved: true,
        resolved_at: new Date().toISOString(),
        resolved_by: (await supabase.auth.getUser()).data.user?.id
      })
      .eq('id', alertId);

    if (error) {
      console.error('Error resolving system alert:', error);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Error resolving system alert:', error);
    throw error;
  }
};

/**
 * Get business health metrics
 */
export const getBusinessHealth = async () => {
  try {
    console.log('Fetching business health data...');
    
    // Get data from multiple sources to build comprehensive health metrics
    const [saasMetrics, gamesResult, studiosResult, contactStats] = await Promise.all([
      supabase.rpc('get_saas_metrics_summary'),
      supabase.from('games').select('id, created_at, tier, status').eq('status', 'published'),
      supabase.from('gaming_studios').select('id'),
      supabase.rpc('get_contact_stats')
    ]);
    
    console.log('Business health data sources:', {
      saasMetrics: saasMetrics.data,
      games: gamesResult.data?.length,
      studios: studiosResult.data?.length,
      contactStats: contactStats.data
    });

    // Calculate engagement score based on DAU/MAU ratio and activity
    const dau = saasMetrics.data?.dau || 0;
    const mau = saasMetrics.data?.mau || 0;
    const engagementScore = mau > 0 ? Math.round((dau / mau) * 100) : 0;

    // Count new games this month and calculate premium vs free ratio
    const thisMonth = new Date();
    thisMonth.setDate(1);
    const games = gamesResult.data || [];
    const newGamesThisMonth = games.filter(game => 
      new Date(game.created_at) >= thisMonth
    ).length;
    
    const premiumGames = games.filter(game => game.tier === 'premium').length;
    const freeGames = games.filter(game => game.tier === 'free').length;

    // Get support metrics from contact stats - the RPC function returns an array with one row
    const supportStats = (contactStats.data && contactStats.data[0]) || {};

    const result = {
      engagementScore: engagementScore,
      monthlyActiveUsers: mau,
      totalGames: games.length,
      premiumGames: premiumGames,
      freeGames: freeGames,
      premiumRatio: games.length > 0 ? Math.round((premiumGames / games.length) * 100) : 0,
      newGamesThisMonth: newGamesThisMonth,
      activeStudios: studiosResult.data?.length || 0,
      supportMetrics: {
        openTickets: supportStats.open_contacts || 0,
        avgResolutionHours: Math.round(supportStats.avg_resolution_hours || 0),
        resolvedToday: supportStats.resolved_today || 0,
        highPriorityOpen: supportStats.high_priority_open || 0
      }
    };
    
    console.log('Business health result:', result);
    return result;
  } catch (error) {
    console.error('Error getting business health:', error);
    // Return meaningful fallback data that matches component expectations
    return {
      engagementScore: 0,
      monthlyActiveUsers: 0,
      totalGames: 0,
      premiumGames: 0,
      freeGames: 0,
      premiumRatio: 0,
      newGamesThisMonth: 0,
      activeStudios: 0,
      supportMetrics: {
        openTickets: 0,
        avgResolutionHours: 0,
        resolvedToday: 0,
        highPriorityOpen: 0
      }
    };
  }
};

/**
 * Get top performing games with real analytics data
 */
export const getTopPerformingGames = async () => {
  try {
    // Fetch games with studio info
    const { data: games, error: gamesError } = await supabase
      .from('games')
      .select(`
        id,
        title,
        cover_image,
        studio_id,
        gaming_studios!inner(name)
      `)
      .eq('status', 'published')
      .limit(50); // Get more games to calculate performance

    if (gamesError) {
      console.error('Error fetching games:', gamesError);
      return [];
    }

    if (!games || games.length === 0) {
      return [];
    }

    // Fetch play counts from analytics_events (game_start events)
    const { data: playData, error: playError } = await supabase
      .from('analytics_events')
      .select('game_id, event_type')
      .eq('event_type', 'game_start')
      .in('game_id', games.map(g => g.id));

    if (playError) {
      console.error('Error fetching play counts:', playError);
    }

    // Count plays per game
    const playCountMap = {};
    playData?.forEach(event => {
      playCountMap[event.game_id] = (playCountMap[event.game_id] || 0) + 1;
    });

    // Fetch ratings from game_reviews
    const { data: reviewData, error: reviewError } = await supabase
      .from('game_reviews')
      .select('game_id, rating')
      .in('game_id', games.map(g => g.id));

    if (reviewError) {
      console.error('Error fetching reviews:', reviewError);
    }

    // Calculate average ratings per game
    const ratingMap = {};
    reviewData?.forEach(review => {
      if (!ratingMap[review.game_id]) {
        ratingMap[review.game_id] = { sum: 0, count: 0 };
      }
      ratingMap[review.game_id].sum += review.rating;
      ratingMap[review.game_id].count += 1;
    });

    // Transform and calculate performance scores
    const gamesWithMetrics = games.map(game => {
      const playCount = playCountMap[game.id] || 0;
      const ratingData = ratingMap[game.id];
      const avgRating = ratingData ? ratingData.sum / ratingData.count : 0;

      return {
        id: game.id,
        title: game.title,
        studio: game.gaming_studios?.name || 'Unknown Studio',
        coverImage: game.cover_image || '/assets/images/no_image.png',
        rating: avgRating,
        playCount: playCount,
        // Performance score: weighted combination of plays and ratings
        performanceScore: (playCount * 0.7) + (avgRating * 100 * 0.3)
      };
    });

    // Sort by performance score and return top 5
    return gamesWithMetrics
      .sort((a, b) => b.performanceScore - a.performanceScore)
      .slice(0, 5);

  } catch (error) {
    console.error('Error getting top performing games:', error);
    return [];
  }
};

/**
 * Get studio performance data with real metrics
 */
export const getStudioPerformance = async () => {
  try {
    // Fetch studios with games
    const { data: studios, error: studiosError } = await supabase
      .from('gaming_studios')
      .select(`
        id,
        name,
        logo_url,
        games(id, status)
      `)
      .limit(20);

    if (studiosError) {
      console.error('Error fetching studios:', studiosError);
      return [];
    }

    if (!studios || studios.length === 0) {
      return [];
    }

    // Get all published game IDs per studio
    const studioGameMap = {};
    studios.forEach(studio => {
      const publishedGames = studio.games?.filter(g => g.status === 'published') || [];
      studioGameMap[studio.id] = publishedGames.map(g => g.id);
    });

    // Get all game IDs across all studios
    const allGameIds = Object.values(studioGameMap).flat();

    if (allGameIds.length === 0) {
      return studios.map(studio => ({
        id: studio.id,
        name: studio.name,
        logoUrl: studio.logo_url,
        gameCount: 0,
        totalPlays: 0,
        avgRating: 0,
        totalRevenue: 0
      }));
    }

    // Fetch play counts from analytics_events
    const { data: playData, error: playError } = await supabase
      .from('analytics_events')
      .select('game_id, event_type')
      .eq('event_type', 'game_start')
      .in('game_id', allGameIds);

    if (playError) {
      console.error('Error fetching play counts:', playError);
    }

    // Count plays per game
    const playCountMap = {};
    playData?.forEach(event => {
      playCountMap[event.game_id] = (playCountMap[event.game_id] || 0) + 1;
    });

    // Fetch ratings from game_reviews
    const { data: reviewData, error: reviewError } = await supabase
      .from('game_reviews')
      .select('game_id, rating')
      .in('game_id', allGameIds);

    if (reviewError) {
      console.error('Error fetching reviews:', reviewError);
    }

    // Calculate average ratings per game
    const ratingMap = {};
    reviewData?.forEach(review => {
      if (!ratingMap[review.game_id]) {
        ratingMap[review.game_id] = { sum: 0, count: 0 };
      }
      ratingMap[review.game_id].sum += review.rating;
      ratingMap[review.game_id].count += 1;
    });

    // Fetch revenue from revenue_transactions
    const { data: revenueData, error: revenueError } = await supabase
      .from('revenue_transactions')
      .select('studio_id, studio_revenue')
      .in('studio_id', studios.map(s => s.id));

    if (revenueError) {
      console.error('Error fetching revenue:', revenueError);
    }

    // Calculate total revenue per studio
    const revenueMap = {};
    revenueData?.forEach(transaction => {
      revenueMap[transaction.studio_id] = (revenueMap[transaction.studio_id] || 0) + parseFloat(transaction.studio_revenue || 0);
    });

    // Transform and calculate studio metrics
    const studiosWithMetrics = studios.map(studio => {
      const gameIds = studioGameMap[studio.id] || [];
      const totalPlays = gameIds.reduce((sum, gameId) => sum + (playCountMap[gameId] || 0), 0);

      // Calculate average rating across all studio games
      let totalRatingSum = 0;
      let totalRatingCount = 0;
      gameIds.forEach(gameId => {
        if (ratingMap[gameId]) {
          totalRatingSum += ratingMap[gameId].sum;
          totalRatingCount += ratingMap[gameId].count;
        }
      });
      const avgRating = totalRatingCount > 0 ? totalRatingSum / totalRatingCount : 0;

      const totalRevenue = revenueMap[studio.id] || 0;

      return {
        id: studio.id,
        name: studio.name,
        logoUrl: studio.logo_url,
        gameCount: gameIds.length,
        totalPlays: totalPlays,
        avgRating: avgRating,
        totalRevenue: totalRevenue,
        // Performance score: weighted combination of plays, ratings, and revenue
        performanceScore: (totalPlays * 0.4) + (avgRating * 500 * 0.3) + (totalRevenue * 0.3)
      };
    });

    // Sort by performance score and return top 5
    return studiosWithMetrics
      .sort((a, b) => b.performanceScore - a.performanceScore)
      .slice(0, 5);

  } catch (error) {
    console.error('Error getting studio performance:', error);
    return [];
  }
};

/**
 * Get revenue analytics with real data from revenue_transactions
 */
export const getRevenueAnalytics = async (timeRange = '30d') => {
  try {
    // Calculate date range
    const endDate = new Date();
    const startDate = new Date();

    switch (timeRange) {
      case '7d':
        startDate.setDate(startDate.getDate() - 7);
        break;
      case '30d':
        startDate.setDate(startDate.getDate() - 30);
        break;
      case '90d':
        startDate.setDate(startDate.getDate() - 90);
        break;
      default:
        startDate.setDate(startDate.getDate() - 30);
    }

    // Fetch revenue transactions
    const { data: transactions, error } = await supabase
      .from('revenue_transactions')
      .select('gross_amount, studio_revenue, transaction_date')
      .gte('transaction_date', startDate.toISOString())
      .lte('transaction_date', endDate.toISOString())
      .order('transaction_date', { ascending: true });

    if (error) {
      console.error('Error fetching revenue transactions:', error);
      return [];
    }

    if (!transactions || transactions.length === 0) {
      return [];
    }

    // Group by date
    const revenueByDate = {};
    transactions.forEach(t => {
      const date = new Date(t.transaction_date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });

      if (!revenueByDate[date]) {
        revenueByDate[date] = {
          date,
          revenue: 0,
          payouts: 0
        };
      }

      revenueByDate[date].revenue += parseFloat(t.gross_amount || 0);
      revenueByDate[date].payouts += parseFloat(t.studio_revenue || 0);
    });

    // Convert to array and sort by date
    return Object.values(revenueByDate);

  } catch (error) {
    console.error('Error getting revenue analytics:', error);
    return [];
  }
};

/**
 * Get SaaS metrics summary
 */
export const getSaaSMetricsSummary = async () => {
  try {
    console.log('Calling get_saas_metrics_summary RPC function...');
    const { data, error } = await supabase.rpc('get_saas_metrics_summary');
    
    console.log('RPC Response:', { data, error });
    
    if (error) {
      console.error('Error fetching SaaS metrics:', error);
      return {
        mrr: 0,
        arr: 0,
        churnRate: 0,
        ltv: 0,
        cac: 0,
        activeUsers: 0
      };
    }

    console.log('Parsed SaaS metrics:', data);

    return {
      mrr: data?.mrr || 0,
      arr: data?.arr || 0,
      churnRate: data?.churn_rate || 0,
      ltv: data?.mrr ? (data.mrr / (data.churn_rate / 100 || 1)) : 0, // Simple LTV calculation
      cac: 85, // Would need marketing spend data
      activeUsers: data?.mau || 0,
      active_subscriptions: data?.active_subscriptions || 0,
      dau: data?.dau || 0,
      mau: data?.mau || 0,
      stickiness: data?.stickiness || 0,
      trial_conversion_rate: data?.trial_conversion_rate || 0
    };
  } catch (error) {
    console.error('Error getting SaaS metrics summary:', error);
    return {
      mrr: 0,
      arr: 0,
      churnRate: 0,
      ltv: 0,
      cac: 0,
      activeUsers: 0
    };
  }
};

/**
 * Get monthly targets
 */
export const getMonthlyTargets = async () => {
  try {
    const currentDate = new Date();
    const { data: targets, error } = await supabase
      .from('monthly_targets')
      .select('*')
      .eq('year', currentDate.getFullYear())
      .eq('month', currentDate.getMonth() + 1);

    if (error) {
      console.error('Error fetching monthly targets:', error);
      return {
        revenue: { target: 15000, current: 0 },
        subscribers: { target: 300, current: 0 },
        churn: { target: 2.5, current: 0 }
      };
    }

    // Get current metrics for comparison
    const { data: currentMetrics } = await supabase.rpc('get_saas_metrics_summary');

    const revenueTarget = targets?.find(t => t.metric_name === 'mrr');
    const subscriberTarget = targets?.find(t => t.metric_name === 'active_subscriptions');
    const churnTarget = targets?.find(t => t.metric_name === 'churn_rate');

    return {
      revenue: { 
        target: revenueTarget?.target_value || 15000, 
        current: currentMetrics?.mrr || 0 
      },
      subscribers: { 
        target: subscriberTarget?.target_value || 300, 
        current: currentMetrics?.active_subscriptions || 0 
      },
      churn: { 
        target: churnTarget?.target_value || 2.5, 
        current: currentMetrics?.churn_rate || 0 
      }
    };
  } catch (error) {
    console.error('Error getting monthly targets:', error);
    throw error;
  }
};

/**
 * Set monthly target
 */
export const setMonthlyTarget = async (metric, value) => {
  try {
    const currentDate = new Date();
    const { error } = await supabase
      .from('monthly_targets')
      .upsert({
        year: currentDate.getFullYear(),
        month: currentDate.getMonth() + 1,
        metric_name: metric,
        target_value: value,
        updated_at: new Date().toISOString()
      });

    if (error) {
      console.error('Error setting monthly target:', error);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Error setting monthly target:', error);
    throw error;
  }
};
